import {
    Box,
    Divider,
    IconButton,
    LabeledIconButton,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./navigation-icons-menu.scss";
import { useIsMobileViewport } from "components/navigation/hooks/useIsMobileViewport";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import {
    MainMenuItem,
    useMainMenuItems,
} from "components/navigation/navigation-panel/main-navigation-panel/navigation-icons-menu/useMainMenuItems";
import { getAdvertisedRouteTooltipContent } from "components/navigation/navigation-panel/sub-navigation-panel/contents/navigation-sub-menu-item/getAdvertisedRouteTooltipContent";
import { ASSET_MANAGEMENT_ROUTE_BMP } from "components/router/Routes";

const TOOLTIP_DISTANCE_PIXELS = 4 + 2;
interface Props {
    onMenuItemSelect: (item: MainMenuItem) => void;
}

interface MenuItemButtonProps {
    item: MainMenuItem;
    isActive: boolean;
    isDisabledAndAdvertised: boolean;
    onSelect: (item: MainMenuItem) => void;
}

const MenuItemButton = (props: MenuItemButtonProps) => {
    const { t } = useTranslation();
    const label = t(props.item.title);
    const isMobileViewport = useIsMobileViewport();

    const buttonProps = {
        className: classNames(style.navMenuIcon, { active: props.isActive }),
        onClick: () => props.onSelect(props.item),
        testId: props.item.testId,
        "aria-label": label,
        disabled: props.isDisabledAndAdvertised,
        children: props.item.icon,
    };

    const button = isMobileViewport ? (
        <LabeledIconButton {...buttonProps} label={label} />
    ) : (
        <IconButton {...buttonProps} />
    );

    if (props.isDisabledAndAdvertised) {
        return (
            <Tooltip>
                <TooltipContent>{getAdvertisedRouteTooltipContent(props.item.mainRoute.path)}</TooltipContent>
                <TooltipTrigger>{button}</TooltipTrigger>
            </Tooltip>
        );
    }

    if (isMobileViewport) {
        return button;
    }

    return (
        <Tooltip placement="right" distance={TOOLTIP_DISTANCE_PIXELS}>
            <TooltipContent>{label}</TooltipContent>
            <TooltipTrigger>{button}</TooltipTrigger>
        </Tooltip>
    );
};

const NavigationMenuIcons = (props: Props) => {
    const { items, activeMainMenuItem } = useMainMenuItems();
    const isRouteEnabled = useIsRouteEnabled();

    const createMenuItemButton = (item: MainMenuItem) => {
        if (!isRouteEnabled(item.mainRoute) && !item.mainRoute.advertised) {
            return null;
        }

        const isDisabledAndAdvertised = !isRouteEnabled(item.mainRoute) && (item.mainRoute.advertised ?? false);

        if (item.mainRoute === ASSET_MANAGEMENT_ROUTE_BMP) {
            return (
                <li key={item.title} className={style.badgedMenuItem}>
                    <Box columnLayout className={style.dividerContainer}>
                        <Divider className={style.divider} />
                    </Box>

                    <MenuItemButton
                        item={item}
                        isActive={item === activeMainMenuItem}
                        isDisabledAndAdvertised={isDisabledAndAdvertised}
                        onSelect={props.onMenuItemSelect}
                    />

                    {!isDisabledAndAdvertised && <div className={style.newBadge} />}
                </li>
            );
        }

        return (
            <li key={item.title}>
                <MenuItemButton
                    item={item}
                    isActive={item === activeMainMenuItem}
                    isDisabledAndAdvertised={isDisabledAndAdvertised}
                    onSelect={props.onMenuItemSelect}
                />
            </li>
        );
    };

    return (
        <nav className={classNames(style.navigationMenu, style.mainNavigation)}>
            <ul>{items.map((item) => createMenuItemButton(item))}</ul>
        </nav>
    );
};

export default NavigationMenuIcons;
